import { MakeItABundleProps } from "@bluebottlecoffee/design-system/components/modules/Conversion/components/MakeItABundle";
import { useCart } from "@chordcommerce/react-autonomy";
import { Dispatch, useState } from "react";
import { DialogContentTypeNullable } from "@bluebottlecoffee/design-system";
import { ConversionSchema } from "../transformers/conversion";
import { getPriceText } from "../utils";
import useVariantPrice from "../chord/hooks/components/use-variant-price";
import useVariantAvailability from "../chord/hooks/components/use-variant-availability";
import { toImageProps } from "../transformers/image";
import { toVariantProps } from "../transformers/variant";
import { DereferencedVariant } from "../sanity/variant-queries";

interface BundleTransformType {
  setDialogContentType: Dispatch<DialogContentTypeNullable>;
  variantOne: DereferencedVariant;
  variantTwo: DereferencedVariant;
  bundleVariant: DereferencedVariant;
  lang: string;
  copy: {
    loadingMessage: string;
  };
}

const imagePlaceholder = {
  src: "/v1612883439/blue_bottle_brand_assets/Bottle_Logo-Small.001_031820.png",
  altText: "Placeholder",
};

const getFinalPrice = (variant: DereferencedVariant, lang: string) => {
  const productFirstVariant = toVariantProps(variant, lang);

  const productVariantInput = {
    sku: productFirstVariant.sku,
    quantity: 1,
  };

  const productTwoPrice = productFirstVariant.price.amount;
  const { discountPrice: productDiscountPrice } =
    useVariantPrice(productVariantInput);

  return productDiscountPrice < productTwoPrice
    ? productDiscountPrice
    : productTwoPrice;
};

export const useBundle = ({
  setDialogContentType,
  variantOne,
  variantTwo,
  bundleVariant,
  lang,
  copy: { loadingMessage },
}: BundleTransformType): MakeItABundleProps | null => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { addToCart } = useCart();

  // Bundle Prices
  const bundleFirstVariant = toVariantProps(bundleVariant, lang);

  const bundleVariantInput = {
    sku: bundleFirstVariant.sku,
    quantity: 1,
  };

  const bundlePrice = bundleFirstVariant.price.amount;
  const { discountPrice: bundleDiscountPrice } =
    useVariantPrice(bundleVariantInput);

  const bundleFinalPrice =
    bundleDiscountPrice < bundlePrice ? bundleDiscountPrice : bundlePrice;

  // Variant One Final Price
  const variantOneFinalPrice = getFinalPrice(variantOne, lang);

  // Variant Two Final Price
  const variantTwoFinalPrice = getFinalPrice(variantTwo, lang);

  // Bundle & Save Price
  const bundleSavePrice = getPriceText({
    amount: variantOneFinalPrice + variantTwoFinalPrice - bundleFinalPrice,
    currency: bundleFirstVariant.price.currency,
  });

  // Stock bundle
  const { totalOnHand, isFetchingAvailability } = useVariantAvailability({
    shouldFetch: !!bundleFirstVariant.inventoryLimited,
    sku: bundleFirstVariant.sku,
  });

  const isVariantSoldOut: boolean = isFetchingAvailability && totalOnHand <= 0;

  // Discount applied compared to the total price of the two main products.
  const bundleVariantData = {
    ...bundleFirstVariant,
    regularPrice: {
      amount: variantOneFinalPrice + variantTwoFinalPrice,
      currency: bundleFirstVariant.regularPrice.currency,
    },
  };

  const handleAddToCart = async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      await addToCart({
        sku: bundleVariant.sku,
        quantity: 1,
      });

      setDialogContentType("minicart");
    } catch (error) {
      console.error(error);
      setDialogContentType(null);
      setErrorMessage(error.message);
    }

    setLoading(false);
  };

  if (isVariantSoldOut) {
    return null;
  }

  return {
    bundleText: `Bundle & Save ${bundleSavePrice}`,
    title: "Make It A Set",
    buttonText: "Add to cart",
    productOne: {
      image: variantOne.images?.[0]
        ? toImageProps(variantOne.images[0], lang)
        : imagePlaceholder,
      productName: variantOne.label[lang],
    },
    productTwo: {
      image: variantTwo.images?.[0]
        ? toImageProps(variantTwo.images[0], lang)
        : imagePlaceholder,
      productName: variantTwo.label[lang],
    },
    bundle: {
      variant: bundleVariantData,
      quantity: 1,
      discountPrice: bundleDiscountPrice,
      onClick: handleAddToCart,
    },
    addToCartProps: {
      errorMessage,
      loadingMessage,
      loading,
    },
  };
};

interface BundleProps {
  conversion: ConversionSchema;
  setDialogContentType: Dispatch<DialogContentTypeNullable>;
  lang: string;
  copy: {
    adding: string;
  };
}

export const toBundle = ({
  conversion,
  setDialogContentType,
  lang,
  copy: { adding },
}: BundleProps) => {
  const { enabledBundle, bundleVariant, variantOne, variantTwo } =
    conversion.bundle ?? {};

  if (!enabledBundle) return undefined;
  if (!bundleVariant.forSale || !variantOne.forSale || !variantTwo.forSale)
    return undefined;

  return () =>
    useBundle({
      ...conversion.bundle,
      setDialogContentType,
      lang,
      copy: {
        loadingMessage: adding,
      },
    });
};
